/**
 * ordenMesaEstados.ts — Catálogo de estados de una orden de mesa.
 *
 * QUÉ EXPORTA: ORDEN_ESTADO_META, ESTADOS_ORDEN_ACTIVA, ESTADOS_KDS,
 *   SIGUIENTE_ESTADO_KDS, ORDEN_ESTADOS_FILTRO.
 * QUÉ NO DEBE HACER: No conoce React ni servicios; solo etiquetas y orden.
 */
import type { EstadoOrdenMesa } from "../types/mesas.types";

export interface OrdenEstadoMeta {
  label: string;
  /** Clases del badge (fondo/texto). */
  badgeClass: string;
  /** Clase del punto indicador. */
  dotClass: string;
}

export const ORDEN_ESTADO_META: Record<EstadoOrdenMesa, OrdenEstadoMeta> = {
  abierta: {
    label: "Borrador",
    badgeClass:
      "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
    dotClass: "bg-slate-400",
  },
  enviada: {
    label: "Enviada",
    badgeClass:
      "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    dotClass: "bg-blue-500",
  },
  preparando: {
    label: "En preparación",
    badgeClass:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    dotClass: "bg-amber-500",
  },
  lista: {
    label: "Lista",
    badgeClass:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
    dotClass: "bg-emerald-500",
  },
  servida: {
    label: "Servida",
    badgeClass:
      "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300",
    dotClass: "bg-cyan-500",
  },
  por_cobrar: {
    label: "Por cobrar",
    badgeClass:
      "bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300",
    dotClass: "bg-fuchsia-500",
  },
  cobrada: {
    label: "Cobrada",
    badgeClass:
      "bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-400",
    dotClass: "bg-gray-400",
  },
  anulada: {
    label: "Anulada",
    badgeClass: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    dotClass: "bg-red-500",
  },
};

/** Estados que mantienen la orden "viva" (mesa ocupada). */
export const ESTADOS_ORDEN_ACTIVA: EstadoOrdenMesa[] = [
  "abierta",
  "enviada",
  "preparando",
  "lista",
  "servida",
  "por_cobrar",
];

/** Estados que la cocina (KDS) debe ver y avanzar. */
export const ESTADOS_KDS: EstadoOrdenMesa[] = [
  "enviada",
  "preparando",
  "lista",
];

/** Transición que aplica el KDS al tocar una tarjeta. */
export const SIGUIENTE_ESTADO_KDS: Partial<
  Record<EstadoOrdenMesa, EstadoOrdenMesa>
> = {
  enviada: "preparando",
  preparando: "lista",
};

/** Opciones del filtro de la lista de órdenes (valor + etiqueta). */
export const ORDEN_ESTADOS_FILTRO: {
  value: EstadoOrdenMesa | "activas";
  label: string;
}[] = [
  { value: "activas", label: "Activas" },
  { value: "enviada", label: "Enviadas" },
  { value: "preparando", label: "En preparación" },
  { value: "lista", label: "Listas" },
  { value: "servida", label: "Servidas" },
  { value: "por_cobrar", label: "Por cobrar" },
  { value: "cobrada", label: "Cobradas" },
  { value: "anulada", label: "Anuladas" },
];

/** ¿La orden se puede cobrar/importar en Caja? */
export const puedeCobrarse = (estado: EstadoOrdenMesa): boolean =>
  ESTADOS_ORDEN_ACTIVA.includes(estado);
